import { Can } from '@/components/can/Can';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useBasicStoresList } from '@/features/store-management/basic-stores/hooks/useBasicStoresList';
import { BasicStoreManagementTable } from '@/features/store-management/basic-stores/table';
import { Translation } from '@/hoslog-intl';
import { PlusIcon } from '@/icons/plus-icon';
import { createFileRoute, Link, SearchSchemaInput } from '@tanstack/react-router';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum(['storeNameEng', 'telephone', 'prefecture', 'status', 'createdDate', 'updatedDate']),
  desc: z.boolean(),
});

const BasicStoreSearchSchema = z.object({
  sort: z
    .preprocess((val) => {
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return undefined;
        }
      }
      return val;
    }, z.array(sortItemSchema))
    .optional(),

  createdDate: z
    .string()

    .optional(),

  q: z
    .any()
    .transform((val) => (val == null ? undefined : String(val)))
    .optional(),

  status: z
    .string()
    .transform((val) => val.split(','))
    .pipe(z.array(z.enum(['Active', 'Blocked'])))
    .optional(),

  page: z.coerce.number().positive().catch(0),
  location: z.string().optional(),
  fetch: z.coerce.number().positive().max(100).catch(10),
});

export const Route = createFileRoute('/(app)/store-information/basic-stores/')({
  component: RouteComponent,
  loader: () => {
    const renderActions = (
      <div>
        <Can rule={{ allOf: [PERMS.club.basic.create] }}>
          <Link
            to="/store-information/basic-stores/add-store"
            search={() => ({ breadcrumbReturnTo: window.location.href })}
          >
            <Button type="submit" className="w-[110px]">
              <PlusIcon /> <Translation id="storeManagement.form.button.addNew" />
            </Button>
          </Link>
        </Can>
      </div>
    );
    return {
      actions: { renderActions },
    };
  },
  validateSearch: (search: SearchSchemaInput) => BasicStoreSearchSchema.parse(search),
});

function RouteComponent() {
  const { basicStores, isLoading, pagination } = useBasicStoresList();
  return <BasicStoreManagementTable data={basicStores} isLoading={isLoading} pagination={pagination} />;
}
